import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaSearch,
  FaFileDownload,
  FaSpinner,
  FaTicketAlt,
  FaCalendarAlt,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaChartLine,
  FaFilter,
  FaSync,
  FaEye,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaGift,
  FaRupeeSign,
  FaPercent,
  FaClock
} from 'react-icons/fa';

const CouponHistory = () => {
  const [coupons, setCoupons] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showTodayOnly, setShowTodayOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);



  // Fetch coupon history on component mount
  useEffect(() => {
    const fetchCouponHistory = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/coupon/history`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Coupon history response:", response);
        // The API response structure is: response.data.data (array)
        setCoupons(Array.isArray(response.data.data) ? response.data.data : []);
        setError(null);
      } catch (err) {
        console.error("Error fetching coupon history:", err);
        setError("Failed to load coupon history. Please try again later.");
        setCoupons([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCouponHistory();
  }, []);

  // Function to filter data between start and end dates
  const filterDataByDateRange = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }

    // Validate date range
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      alert("Start date cannot be after end date");
      return;
    }
  };

  // Reset date filters
  const clearDateFilters = () => {
    setStartDate("");
    setEndDate("");
    setShowTodayOnly(false);
  };

  // Handle Today checkbox
  const handleTodayCheckbox = (e) => {
    const isChecked = e.target.checked;
    setShowTodayOnly(isChecked);

    if (isChecked) {
      // Set today's date for both start and end using local timezone
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`; // Format: YYYY-MM-DD
      setStartDate(formattedDate);
      setEndDate(formattedDate);
    } else {
      // Clear the dates if unchecked
      setStartDate("");
      setEndDate("");
    }
  };

  // Filter coupons by code, user name, amount, or date
  const filteredCoupons = coupons.filter((coupon) => {
    // Add null checks for coupon object
    if (!coupon) return false;

    const adId = coupon._id?.toLowerCase() || "";
    const adType = coupon.adType?.toLowerCase() || "";
    const couponCode = coupon.couponCode?.toLowerCase() || "";
    const iinsafId = coupon.owner?.iinsafId?.toLowerCase() || "";
    const userName = coupon.owner?.name?.toLowerCase() || "";
    const email = coupon.owner?.email?.toLowerCase() || "";
    const mobile = coupon.owner?.mobile?.toLowerCase() || "";
    const description = coupon.mediaDescription?.toLowerCase() || "";
    const status = coupon.status?.toLowerCase() || "";
    const totalCost = coupon.totalCost?.toString() || "";
    const date = coupon.createdAt
      ? new Date(coupon.createdAt).toLocaleDateString().toLowerCase()
      : "";
    const search = searchTerm.toLowerCase();

    // Apply text search filter
    const matchesSearch = !searchTerm ||
      adId.includes(search) ||
      adType.includes(search) ||
      couponCode.includes(search) ||
      iinsafId.includes(search) ||
      userName.includes(search) ||
      email.includes(search) ||
      mobile.includes(search) ||
      description.includes(search) ||
      status.includes(search) ||
      totalCost.includes(search) ||
      date.includes(search);

    // Apply date range filter
    const couponDate = coupon.createdAt ? new Date(coupon.createdAt) : null;

    // Create new date objects to avoid mutating the original dates
    const filterStartDate = startDate ? new Date(startDate + 'T00:00:00') : null;
    const filterEndDate = endDate ? new Date(endDate + 'T23:59:59.999') : null;

    const withinDateRange =
      (!filterStartDate || (couponDate && couponDate >= filterStartDate)) &&
      (!filterEndDate || (couponDate && couponDate <= filterEndDate));

    return matchesSearch && withinDateRange;
  });

  // Calculate statistics
  const stats = {
    total: coupons.length,
    filtered: filteredCoupons.length,
    totalCost: filteredCoupons.reduce((sum, coupon) => sum + (parseFloat(coupon.totalCost) || 0), 0),
    completed: filteredCoupons.filter(c => c.status?.toLowerCase() === 'completed').length,
    pending: filteredCoupons.filter(c => c.status?.toLowerCase() === 'pending').length,
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCoupons.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCoupons.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Export to CSV
  const exportToCSV = () => {
    if (filteredCoupons.length === 0) {
      alert("No data to export");
      return;
    }

    // Create CSV header row
    const headers = [
      "Ad ID",
      "Ad Type",
      "Coupon Code",
      "Iinsaf ID",
      "Name",
      "Email",
      "Mobile",
      "Description",
      "Status",
      "Total Cost",
      "Created Date"
    ].join(",");

    // Create CSV data rows
    const csvData = filteredCoupons.map((coupon) => {
      return [
        coupon._id || "",
        coupon.adType || "",
        coupon.couponCode || "",
        coupon.owner?.iinsafId || "",
        coupon.owner?.name || "",
        coupon.owner?.email || "",
        coupon.owner?.mobile || "",
        coupon.mediaDescription || "",
        coupon.status || "",
        coupon.totalCost || "0",
        coupon.createdAt ? formatDate(coupon.createdAt) : ""
      ].join(",");
    });

    // Combine header and data
    const csv = [headers, ...csvData].join("\n");

    // Create a Blob and download link
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", `coupon_history_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-emerald-700 via-teal-600 to-cyan-600 shadow-xl overflow-hidden relative">
        <div className="absolute inset-0 bg-white/5 backdrop-blur-3xl"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse delay-700"></div>

        <div className="px-4 sm:px-6 lg:px-8 py-10 relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex items-center">
              <div className="p-4 bg-white/20 backdrop-blur-md rounded-2xl shadow-inner border border-white/30">
                <FaTicketAlt className="h-10 w-10 text-white drop-shadow-lg" />
              </div>
              <div className="ml-6">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight drop-shadow-md">
                  Coupon History
                </h1>
                <p className="text-emerald-100 text-lg sm:text-xl font-medium opacity-90">
                  Track and analyze coupon usage across your platform
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={exportToCSV}
                disabled={filteredCoupons.length === 0}
                className="group inline-flex items-center px-6 py-3 bg-white text-emerald-600 rounded-xl hover:bg-emerald-50 transition-all duration-300 shadow-[0_10px_20px_-5px_rgba(0,0,0,0.2)] hover:shadow-[0_15px_25px_-5px_rgba(0,0,0,0.3)] hover:-translate-y-1 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaFileDownload className="mr-3 group-hover:bounce transition-transform duration-300" />
                Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaChartLine className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Coupons</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaCheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaClock className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaRupeeSign className="h-8 w-8 text-purple-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Cost</p>
                <p className="text-2xl font-bold text-gray-900">₹{stats.totalCost.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-indigo-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaFilter className="h-8 w-8 text-indigo-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Filtered</p>
                <p className="text-2xl font-bold text-gray-900">{stats.filtered}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by ad ID, ad type, coupon code, iinsaf ID, name, email, mobile, description..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex items-center gap-2">
                <FaCalendarAlt className="text-green-600" />
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setShowTodayOnly(false);
                  }}
                  max={endDate || undefined}
                  disabled={showTodayOnly}
                />
                <span className="text-gray-400">to</span>
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setShowTodayOnly(false);
                  }}
                  min={startDate || undefined}
                  disabled={showTodayOnly}
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={filterDataByDateRange}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!startDate || !endDate}
                >
                  Apply
                </button>
                <button
                  onClick={clearDateFilters}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Today Only Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="todayOnly"
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                checked={showTodayOnly}
                onChange={handleTodayCheckbox}
              />
              <label htmlFor="todayOnly" className="ml-2 text-sm font-medium text-gray-700">
                Today Only
              </label>
            </div>

            {/* Refresh Button */}
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <FaSync className="mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Coupons Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center p-8">
              <FaSpinner className="animate-spin text-green-500 text-3xl mr-3" />
              <span className="text-gray-600">Loading coupon history...</span>
            </div>
          ) : filteredCoupons.length === 0 ? (
            <div className="text-center p-8">
              <FaTicketAlt className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No coupon records found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || startDate || endDate
                  ? "Try adjusting your search or filter criteria."
                  : "No coupon usage history available."}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-green-600 to-teal-600">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaCalendarAlt className="w-4 h-4" />
                          Date Created
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaTicketAlt className="w-4 h-4" />
                          Ad Details
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaGift className="w-4 h-4" />
                          Coupon Code
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaUser className="w-4 h-4" />
                          User Details
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaRupeeSign className="w-4 h-4" />
                          Cost & Status
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentItems.map((coupon, index) => (
                      <tr key={coupon._id || index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <FaClock className="mr-2 text-gray-400" />
                            {formatDate(coupon.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-mono font-bold border border-gray-200 w-fit mb-1">
                              {coupon._id || 'N/A'}
                            </span>
                            <span className="text-sm font-semibold text-indigo-600">
                              {coupon.adType || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FaGift className="text-green-500 mr-2" />
                            <span className="font-mono text-sm font-medium text-gray-900">
                              {coupon.couponCode || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center text-sm font-bold text-gray-900">
                              <FaUser className="mr-2 text-indigo-400" />
                              {coupon.owner?.name || 'N/A'}
                            </div>
                            <div className="flex items-center text-xs text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full w-fit">
                              <FaIdCard className="mr-1.5 text-gray-400" />
                              {coupon.owner?.iinsafId || 'N/A'}
                            </div>
                            <div className="flex items-center text-xs text-gray-400 italic">
                              <FaEnvelope className="mr-1.5" />
                              {coupon.owner?.email || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <FaRupeeSign className="mr-2 text-green-500" />
                              <span className="text-sm font-medium text-gray-900">
                                ₹{coupon.totalCost || '0'}
                              </span>
                            </div>
                            <div>
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${coupon.status?.toLowerCase() === 'completed'
                                  ? "bg-green-100 text-green-800"
                                  : coupon.status?.toLowerCase() === 'pending'
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-gray-100 text-gray-800"
                                  }`}
                              >
                                {coupon.status === 'completed' ? (
                                  <FaCheckCircle className="w-3 h-3 mr-1" />
                                ) : coupon.status === 'pending' ? (
                                  <FaClock className="w-3 h-3 mr-1" />
                                ) : (
                                  <FaInfoCircle className="w-3 h-3 mr-1" />
                                )}
                                {coupon.status || 'Unknown'}
                              </span>
                            </div>
                            {coupon.mediaDescription && (
                              <div className="text-xs text-gray-500 max-w-xs truncate">
                                {coupon.mediaDescription}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden">
                {currentItems.map((coupon, index) => (
                  <div key={coupon._id || index} className="border-b border-gray-200 p-4 hover:bg-gray-50">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <FaTicketAlt className="text-green-500 mr-2" />
                          <span className="font-mono text-sm font-medium">
                            {coupon.couponCode || 'N/A'}
                          </span>
                        </div>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${coupon.status?.toLowerCase() === 'completed'
                            ? "bg-green-100 text-green-800"
                            : coupon.status?.toLowerCase() === 'pending'
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-gray-100 text-gray-800"
                            }`}
                        >
                          {coupon.status || 'Unknown'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Ad ID:</span>
                          <div className="font-medium">{coupon._id || 'N/A'}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Cost:</span>
                          <div className="font-medium">₹{coupon.totalCost || '0'}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">User:</span>
                          <div className="font-medium">{coupon.owner?.name || 'N/A'}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Date:</span>
                          <div className="text-xs">{formatDate(coupon.createdAt)}</div>
                        </div>
                      </div>

                      {coupon.mediaDescription && (
                        <div className="text-xs text-gray-500">
                          <span className="font-medium">Description:</span> {coupon.mediaDescription}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row items-center justify-between">
                    <div className="mb-4 sm:mb-0">
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
                        <span className="font-medium">
                          {Math.min(indexOfLastItem, filteredCoupons.length)}
                        </span>{" "}
                        of <span className="font-medium">{filteredCoupons.length}</span> results
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === 1
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                          }`}
                      >
                        Previous
                      </button>

                      <div className="flex space-x-1">
                        {[...Array(totalPages).keys()].map((number) => (
                          <button
                            key={number + 1}
                            onClick={() => paginate(number + 1)}
                            className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === number + 1
                              ? "bg-green-600 text-white"
                              : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                              }`}
                          >
                            {number + 1}
                          </button>
                        ))}
                      </div>

                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === totalPages
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                          }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CouponHistory;
